import { User, UserRole } from '../types/models';

/**
 * Checks if the user has the specified role for a given project/event
 * @param user The user object
 * @param role The role to check for
 * @param projectId Optional project ID for per-event role checking
 * @returns True if the user has the specified role, false otherwise
 */
export const hasRole = (user: User | null, role: UserRole, projectId?: number): boolean => {
  if (!user) return false;
  
  // Check if user has the role in their roles array (per-event roles)
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.includes(role);
  }
  
  return false;
};

/**
 * Checks if the user has admin role
 * @param user The user object
 * @returns True if the user has admin role, false otherwise
 */
export const isAdmin = (user: User | null): boolean => {
  if (!user) return false;
  if (user.is_superuser) return true;
  if (user.is_system_admin) return true;
  return hasRole(user, UserRole.ADMIN);
};

/**
 * Checks if the user has issuer role
 * @param user The user object
 * @returns True if the user has issuer role, false otherwise
 */
export const isIssuer = (user: User | null): boolean => {
  return hasRole(user, UserRole.ISSUER);
};

/**
 * Checks if the user has cm_pic role
 * @param user The user object
 * @returns True if the user has cm_pic role, false otherwise
 */
export const isCmPic = (user: User | null): boolean => {
  return hasRole(user, UserRole.CM_PIC);
};

/**
 * Checks if the user has approver role
 * @param user The user object
 * @returns True if the user has approver role, false otherwise
 */
export const isApprover = (user: User | null): boolean => {
  return hasRole(user, UserRole.APPROVER);
};

/**
 * Checks if the user is the system admin user that should be hidden from the user table
 * @param user The user object
 * @returns True if the user is a system admin user, false otherwise
 */
export const isSystemAdminUser = (user: User | null): boolean => {
  // TODO: Implement per-event admin check
  return false;
};
